import {
  useFindAllHardwareCategoriesQuery,
  useFindOneFirmwareQuery,
  useGetAllTypesQuery,
  useRemoveFirmwareMutation,
} from '../../api/api-hooks';
import { Grid, TextField } from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { Autocomplete } from '@mui/material';
import { FirmwareForm } from './linksTypes';
import useFirmwareCreator from './useFirmwareCreator';
import useFirmwareUpdater from './useFirmwareUpdater';
import { firmwareMiddlware, firmwareValidator } from './linksUtils';
import AnalyzerTypeManagementButton from '../analyzer-types/AnalyzerTypeManagementButton';
import HardwareCategoriesManagementButton from '../hardware-categories/HardwareCategoriesManagementButton';
import { useState } from 'react';

interface Props {
  selectedFirmware: Item[];
  setSelectedFirmware: React.Dispatch<React.SetStateAction<Item[]>>;
}

const addFormInitialState: FirmwareForm = {
  name: undefined,
  link: undefined,
  changelog: undefined,
  size: undefined,
  hardwareType: null,
  userguideName: undefined,
  userguideLink: undefined,
  hardwareCategory: null,
};

const FirmwareOptionsBar = ({
  selectedFirmware,
  setSelectedFirmware,
}: Props) => {
  const [deleteFirmware] = useRemoveFirmwareMutation();
  const firmwareCreator = useFirmwareCreator();
  const firmwareUpdater = useFirmwareUpdater();
  const { data: types } = useGetAllTypesQuery({});
  const { data: categories } = useFindAllHardwareCategoriesQuery({});
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  if (types === undefined || categories === undefined) return null;
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedFirmware}
        setSelectedItems={setSelectedFirmware}
        handlerMiddleware={firmwareMiddlware}
        itemValidator={firmwareValidator}
        itemDeleter={deleteFirmware}
        itemName='Firmware'
        formInstructions='Name, Link, Analyzer Type, Userguide Name, and Userguide Link are required.'
        addFormInitialState={addFormInitialState}
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        childrenCreator={(addFormState: FirmwareForm, handler: any) => (
          <Grid container spacing={4}>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'hardwareCategory');
                  }}
                  value={addFormState.hardwareCategory}
                  options={categories}
                  getOptionLabel={(type) => type.categoryName}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Filter Types By Category'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <HardwareCategoriesManagementButton />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'hardwareType');
                  }}
                  value={addFormState.hardwareType}
                  options={types.filter(
                    (type) =>
                      !addFormState.hardwareCategory ||
                      addFormState.hardwareCategory.id ===
                        type.hardwareCategoryId
                  )}
                  getOptionLabel={(type) => type.typeName}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Hardware Type'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <AnalyzerTypeManagementButton />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='name'
                  label='Firmware Name'
                  fullWidth
                  value={nullStringCheck(addFormState.name)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='link'
                  label='Firmware Link'
                  fullWidth
                  value={nullStringCheck(addFormState.link)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='changelog'
                  label='Changelog Link'
                  fullWidth
                  value={nullStringCheck(addFormState.changelog)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item>
                <TextField
                  margin='dense'
                  name='size'
                  label='Size (MB)'
                  fullWidth
                  value={nullStringCheck(addFormState.size)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='userguideName'
                  label='Userguide Name'
                  fullWidth
                  value={nullStringCheck(addFormState.userguideName)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='userguideLink'
                  label='Userguide Link'
                  fullWidth
                  value={nullStringCheck(addFormState.userguideLink)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={firmwareCreator}
        itemGetter={useFindOneFirmwareQuery}
        itemUpdater={firmwareUpdater}
      />
    </>
  );
};

export default FirmwareOptionsBar;
